import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import './InsuranceRecords.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle, faEye, faEyeSlash,
  faEdit, faTrash,
  faShieldHalved, faFileAlt, faPlus,
  faClipboardList, faCheckCircle,
  faUserGroup, faCalendarDays, faClock,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Container } from 'react-bootstrap';
import insuranceService, { InsurancePolicy, InsurancePolicyDocument } from '../../services/insuranceService';
import { API_BASE_URL } from '../../api/config';
import BackButton from '../../components/BackButton/BackButton';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { useProfile } from '../../context/ProfileContext';
import InsuranceInsights from './InsuranceInsights';

interface RecordType {
  id: number;
  member: string;
  insurer: string;
  policyFrom: string;
  policyTo: string;
  lastUpdateDate: string;
  lastUpdateTime: string;
  status: string;
  expanded?: boolean;
  policyNumber?: string;
  policyName?: string;
  nominee?: string;
  tpa?: string;
  notes?: string;
  documents?: InsurancePolicyDocument[];
}

interface StatItem {
  icon: IconDefinition;
  color: string;
  value: string | number;
  label: string;
}

const InsuranceRecords: React.FC = () => {
  const { t } = useTranslation(['insurance', 'common']);
  const { activeProfile } = useProfile();
  const [records, setRecords] = useState<RecordType[]>([]);
  const [rawPolicies, setRawPolicies] = useState<InsurancePolicy[]>([]);
  const [activeTab, setActiveTab] = useState<'policies' | 'insights'>('policies');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // In-app toast notification (replaces browser alert)
  const [toastMsg, setToastMsg] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'error') => {
    setToastMsg({ message, type });
    setTimeout(() => setToastMsg(null), 4000);
  };

  // Confirm dialog (replaces window.confirm)
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });

  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setLoading(true);
        const policies = await insuranceService.getPolicies();
        setRawPolicies(policies);

        const transformedRecords: RecordType[] = policies.map((policy: InsurancePolicy) => {
          const updatedAt = new Date(policy.updated_at);
          return {
            id: policy.id,
            member: policy.policy_holder_name || (policy.dependant_data?.name) || 'Self',
            insurer: policy.insurance_company,
            policyFrom: formatDate(policy.policy_from),
            policyTo: formatDate(policy.policy_to),
            lastUpdateDate: updatedAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
            lastUpdateTime: updatedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
            status: new Date(policy.policy_to) > new Date() ? 'Active' : 'Expired',
            policyNumber: policy.policy_number,
            policyName: policy.policy_name,
            nominee: policy.nominee,
            tpa: policy.tpa,
            notes: policy.notes,
            documents: policy.documents,
            expanded: false,
          };
        });

        setRecords(transformedRecords);

      } catch (err) {
        console.error('Error fetching insurance policies:', err);
        setError(t('insurance:messages.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, [activeProfile]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleDelete = (index: number) => {
    const record = records[index];
    if (!record.id) return;
    setConfirmDialog({
      open: true,
      title: t('insurance:list.deleteRecord'),
      message: t('insurance:list.deleteRecordConfirm'),
      onConfirm: async () => {
        try {
          await insuranceService.deletePolicy(record.id);
          const updatedRecords = [...records];
          updatedRecords.splice(index, 1);
          setRecords(updatedRecords);
        } catch (err) {
          console.error('Error deleting policy:', err);
          showToast(t('insurance:messages.deleteRecordFailed'));
        }
      },
    });
  };

  const handleToggleExpand = (index: number) => {
    setRecords(prev =>
      prev.map((record, i) =>
        i === index ? { ...record, expanded: !record.expanded } : record
      )
    );
  };

  const activeCount = records.filter(r => r.status === 'Active').length;

  const expiredCount = records.length - activeCount;

  const stats: StatItem[] = [
    { icon: faClipboardList, color: '#06b6d4', value: records.length, label: t('insurance:list.totalPolicies') },
    { icon: faCheckCircle, color: '#10b981', value: activeCount, label: t('insurance:list.active') },
    { icon: faUserGroup, color: '#f59e0b', value: expiredCount, label: t('insurance:list.expired') },
  ];

  if (loading) {
    return (
      <div className="ins-page">
        <div className="ins-loading-container">
          <div className="ins-loading-spinner" />
          <p>{t('insurance:list.loadingRecords')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ins-page">
        <div className="ins-error-container">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>{t('insurance:list.retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ins-page">

      {/* ============ WELCOME BANNER ============ */}
      <section className="ins-welcome-banner">
        <Container>
          <BackButton />
          <div className="ins-welcome-layout">
            <div className="ins-welcome-text">
              <h1 className="ins-welcome-title">
                <FontAwesomeIcon icon={faShieldHalved} />
                {t('insurance:list.insuranceManagement')}
              </h1>
              <p className="ins-welcome-subtitle">
                {t('insurance:list.welcomeSubtitle')}
              </p>
            </div>
            <div className="ins-welcome-actions">
              <button className="ins-welcome-btn primary" onClick={() => navigate("/insurance-records/add")}>
                <FontAwesomeIcon icon={faPlus} />
                {t('insurance:list.addRecord')}
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* ============ STATS ROW ============ */}
      <section className="ins-stats-section">
        <Container>
          <div className="ins-stats-row">
            {stats.map((item, idx) => (
              <div className="ins-stat-card" key={idx}>
                <div className="ins-stat-icon" style={{ background: `${item.color}12`, color: item.color }}>
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="ins-stat-info">
                  <span className="ins-stat-value">{item.value}</span>
                  <span className="ins-stat-label">{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* ============ TAB SWITCHER ============ */}
      <section style={{ padding: '0 0 8px' }}>
        <Container>
          <div style={{ display: 'flex', gap: 8, background: '#f1f5f9', borderRadius: 12, padding: 4, width: 'fit-content' }}>
            <button
              onClick={() => setActiveTab('policies')}
              style={{
                padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, fontFamily: 'Nunito, sans-serif',
                background: activeTab === 'policies' ? '#06b6d4' : 'transparent',
                color: activeTab === 'policies' ? '#fff' : '#64748b',
                transition: 'all 0.2s',
              }}
            >
              <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: 6 }} />
              {t('insurance:list.myPolicies')}
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              style={{
                padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, fontFamily: 'Nunito, sans-serif',
                background: activeTab === 'insights' ? '#8b5cf6' : 'transparent',
                color: activeTab === 'insights' ? '#fff' : '#64748b',
                transition: 'all 0.2s',
              }}
            >
              <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: 6 }} />
              {t('insurance:insights.coverageInsights')}
            </button>
          </div>
        </Container>
      </section>

      {/* ============ INSIGHTS TAB ============ */}
      {activeTab === 'insights' && (
        <section className="ins-content-section">
          <Container>
            <InsuranceInsights policies={rawPolicies} onRefresh={() => window.location.reload()} />
          </Container>
        </section>
      )}

      {/* ============ RECORDS TABLE ============ */}
      {activeTab === 'policies' && <section className="ins-content-section">
        <Container>
          <div className="ins-section-header">
            <h2 className="ins-section-title">
              <FontAwesomeIcon icon={faFileAlt} className="ins-section-title-icon" style={{ color: '#06b6d4' }} />
              {t('insurance:list.policyRecords')}
            </h2>
            <p className="ins-section-subtitle">{t('insurance:list.policyRecordsSubtitle')}</p>
          </div>

          <div className="ins-records-table">
            <div className="ins-records-header">
              <div>{t('insurance:list.member')}</div>
              <div>{t('insurance:list.insurer')}</div>
              <div>{t('insurance:list.policyFrom')}</div>
              <div>{t('insurance:list.policyTo')}</div>
              <div>{t('insurance:list.updated')}</div>
              <div>{t('insurance:list.status')}</div>
              <div>{t('insurance:list.actions')}</div>
            </div>

            {records.length > 0 ? records.map((rec, idx) => (
              <React.Fragment key={idx}>
                <div className="ins-record-row">
                  <div className="ins-cell-member">
                    <FontAwesomeIcon icon={faUserCircle} className="ins-cell-member-icon" />
                    {rec.member}
                  </div>
                  <div>{rec.insurer}</div>
                  <div>
                    <FontAwesomeIcon icon={faCalendarDays} className="ins-cell-date-icon" />
                    {rec.policyFrom}
                  </div>
                  <div>
                    <FontAwesomeIcon icon={faCalendarDays} className="ins-cell-date-icon" />
                    {rec.policyTo}
                  </div>
                  <div className="ins-cell-updated">
                    <span className="ins-cell-date">{rec.lastUpdateDate}</span>
                    <span className="ins-cell-time">
                      <FontAwesomeIcon icon={faClock} /> {rec.lastUpdateTime}
                    </span>
                  </div>
                  <div>
                    <span className={`ins-status-badge ${rec.status === 'Active' ? 'ins-status-active' : 'ins-status-expired'}`}>
                      {rec.status}
                    </span>
                  </div>
                  <div className="ins-actions-cell">
                    <button
                      className="ins-action-view-btn"
                      onClick={() => handleToggleExpand(idx)}
                      title={rec.expanded ? "Hide Details" : "View Details"}
                    >
                      <FontAwesomeIcon
                        icon={rec.expanded ? faEyeSlash : faEye}
                      />
                      <span>
                        {rec.expanded ? "Hide" : "View"}
                      </span>
                    </button>
                    <button
                      className="ins-action-icon-btn"
                      title="Edit"
                      onClick={() => navigate(`/insurance-records/edit/${rec.id}`)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="ins-action-icon-btn danger"
                      title="Delete"
                      onClick={() => handleDelete(idx)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>

                {rec.expanded && (
                  <div className="ins-expanded-details">
                    <div className="ins-detail-item">
                      <span className="ins-detail-label">{t('insurance:list.insuranceCompany')}</span>
                      <span className="ins-detail-value">{rec.insurer || t('insurance:list.na')}</span>
                    </div>
                    <div className="ins-detail-item">
                      <span className="ins-detail-label">{t('insurance:list.policyHolder')}</span>
                      <span className="ins-detail-value">{rec.member}</span>
                    </div>
                    <div className="ins-detail-item">
                      <span className="ins-detail-label">{t('insurance:list.nominee')}</span>
                      <span className="ins-detail-value">{rec.nominee || t('insurance:list.na')}</span>
                    </div>
                    <div className="ins-detail-item">
                      <span className="ins-detail-label">{t('insurance:list.policyNumber')}</span>
                      <span className="ins-detail-value">{rec.policyNumber || t('insurance:list.na')}</span>
                    </div>
                    <div className="ins-detail-item">
                      <span className="ins-detail-label">{t('insurance:list.policyName')}</span>
                      <span className="ins-detail-value">{rec.policyName || t('insurance:list.na')}</span>
                    </div>
                    <div className="ins-detail-item">
                      <span className="ins-detail-label">{t('insurance:list.tpaName')}</span>
                      <span className="ins-detail-value">{rec.tpa || t('insurance:list.na')}</span>
                    </div>
                    <div className="ins-detail-item">
                      <span className="ins-detail-label">{t('insurance:list.notes')}</span>
                      <span className="ins-detail-value">{rec.notes || t('insurance:list.na')}</span>
                    </div>
                    {rec.documents && rec.documents.length > 0 && (
                      <div className="ins-detail-item ins-detail-documents">
                        <span className="ins-detail-label">{t('insurance:list.documents')}</span>
                        <div className="ins-detail-value ins-documents-list">
                          {rec.documents.map((doc) => {
                            const fileUrl = doc.file.startsWith('http') ? doc.file : `${API_BASE_URL}${doc.file}`;
                            const fileName = doc.file_name || doc.file.split('/').pop() || `Document ${doc.id}`;
                            return (
                              <a
                                key={doc.id}
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ins-document-link"
                              >
                                <FontAwesomeIcon icon={faDownload} />
                                {fileName}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )) : (
              <div className="ins-empty-state" style={{ padding: '40px', textAlign: 'center' }}>
                {t('insurance:list.noRecords')}
              </div>
            )}
          </div>
        </Container>
      </section>}

      {/* In-app toast notification */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            padding: '12px 24px',
            borderRadius: 12,
            color: '#fff',
            fontWeight: 500,
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            background: toastMsg.type === 'success' ? '#10b981' : '#ef4444',
          }}
        >
          <span>{toastMsg.message}</span>
          <button
            onClick={() => setToastMsg(null)}
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: 8, fontSize: 14 }}
          >
            ✕
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog(d => ({ ...d, open: false }))}
        cancelLabel={t('insurance:list.cancel')}
        confirmLabel={t('insurance:list.delete')}
        variant="danger"
      />

    </div>
  );
};

export default InsuranceRecords;
